import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { CreateDialogContext } from '@/contexts/CreateDialogContext'
import { AdvertiserEditPage } from '@/pages/advertisers/AdvertiserEditPage'
import { OrderEditPage } from '@/pages/orders/OrderEditPage'
import { LineItemEditPage } from '@/pages/line-items/LineItemEditPage'
import { EarningsAccountEditPage } from '@/pages/earnings-accounts/EarningsAccountEditPage'
import { SiteEditPage } from '@/pages/sites/SiteEditPage'
import { SiteGroupEditPage } from '@/pages/site-groups/SiteGroupEditPage'
import { AdUnitEditPage } from '@/pages/ad-units/AdUnitEditPage'
import { BlocklistEditPage } from '@/pages/blocklists/BlocklistEditPage'
import { RulesetEditPage } from '@/pages/rulesets/RulesetEditPage'
import type { DialogCreatableObject } from './CreateObjectButton'

/** The edit page each object type is created with. */
function editPageFor(type: DialogCreatableObject) {
  switch (type) {
    case 'advertiser':
      return AdvertiserEditPage
    case 'order':
      return OrderEditPage
    case 'lineItem':
      return LineItemEditPage
    case 'earningsAccount':
      return EarningsAccountEditPage
    case 'site':
      return SiteEditPage
    case 'siteGroup':
      return SiteGroupEditPage
    case 'adUnit':
      return AdUnitEditPage
    case 'blocklist':
      return BlocklistEditPage
    case 'ruleset':
      return RulesetEditPage
  }
}

export interface CreateObjectDialogProps {
  type: DialogCreatableObject
  /** The new object's id and parents, as `newObjectUrl` would put them in the URL. */
  id: string
  searchParams: URLSearchParams
  label: string
  onClose: () => void
}

/**
 * Shows an object's create form in a modal over the current page. The edit page
 * renders as usual; inside the dialog its shell drops away and its save bar
 * sticks to the dialog's foot. Saving opens the new object's own page.
 */
export function CreateObjectDialog({ type, id, searchParams, label, onClose }: CreateObjectDialogProps) {
  const { t } = useTranslation()
  const EditPage = editPageFor(type)
  const state = useMemo(() => ({ id, searchParams, close: onClose }), [id, searchParams, onClose])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        // A stray click outside shouldn't throw away a half-filled form.
        onInteractOutside={(e) => e.preventDefault()}
        className="block max-h-[90vh] gap-0 overflow-y-auto bg-background p-0 sm:max-w-4xl [&_.ad-page]:px-6! [&_.ad-page]:pt-6! [&_.ad-page]:pb-0! [&_.detail-savebar]:sticky! [&_.detail-savebar]:inset-x-auto! [&_.detail-savebar]:-mx-6! [&_.detail-savebar]:px-6!"
      >
        <DialogTitle className="sr-only">{t(label)}</DialogTitle>
        <CreateDialogContext.Provider value={state}>
          <EditPage />
        </CreateDialogContext.Provider>
      </DialogContent>
    </Dialog>
  )
}
